import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { profileApi, profileQueryKeys, type Profile } from '@entities/profile';
import { showToast } from '@features/ToastManager';

import { PROFILE_TOAST_SCOPE } from './toast';

import type { IDataResponseError } from '@shared/model/types/dataResponse.inteface';

const getEnabledNotifications = (profile: Profile) =>
    new Set(
        profile.notificationSubscriptions
            .filter(({ isSigned }) => isSigned)
            .map(({ eventName }) => eventName)
    );

export const useNotificationSettings = (profile: Profile) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const subscriptions = profile.notificationSubscriptions;
    const [enabledNotifications, setEnabledNotifications] = useState(() =>
        getEnabledNotifications(profile)
    );

    const mutation = useMutation({
        mutationFn: profileApi.updateNotificationSubscriptions,
        onSuccess: (updatedProfile) => {
            queryClient.setQueryData(profileQueryKeys.current(), updatedProfile);
            showToast(t('profile.page.settings.notifications.success'), {
                scope: PROFILE_TOAST_SCOPE,
            });
        },
        onError: (error) => {
            const responseError = isAxiosError<IDataResponseError>(error)
                ? error.response?.data
                : undefined;
            showToast(
                responseError?.message ?? t('profile.page.settings.notifications.errors.save'),
                { scope: PROFILE_TOAST_SCOPE, type: 'error' }
            );
        },
    });

    const toggleNotification = (eventName: string) => {
        setEnabledNotifications((current) => {
            const next = new Set(current);
            if (next.has(eventName)) {
                next.delete(eventName);
            } else {
                next.add(eventName);
            }
            return next;
        });
    };

    return {
        enabledNotifications,
        toggleNotification,
        selectAll: () =>
            setEnabledNotifications(new Set(subscriptions.map(({ eventName }) => eventName))),
        clearAll: () => setEnabledNotifications(new Set()),
        save: () => mutation.mutate({ 'event_names': Array.from(enabledNotifications) }),
        isPending: mutation.isPending,
    };
};
